/**
 * Reading and writing the operator carried by a filter value.
 *
 * The vocabulary itself lives on the server (App\Search\FilterOperator): each
 * qualifier descriptor ships the operators it accepts, already labelled, so
 * nothing here needs to know what "not_contains" is called in French or whether
 * it takes a value. This module only handles the wire format:
 *
 *   `Braun`               the filter's default operator (contains, or equals)
 *   `>=2026-01-01`        the comparison symbols URLs have always carried
 *   `not_contains:Braun`  a named operator
 *   `empty`               an operator that takes no value
 */

const SYMBOLS = ['>=', '<=', '!=', '>', '<', '='];

// The comparison operators answer to two spellings — the symbol they travel as
// and the name a hand-written URL may use. The server reads both, so this does.
const NAMED_SYMBOLS = { eq: '=', neq: '!=', gt: '>', gte: '>=', lt: '<', lte: '<=' };

const SYMBOL_PATTERN = /^(>=|<=|!=|>|<|=)([\s\S]*)$/;

// A list travels as `a|b`, an escaped pipe as `\|` — the same grammar the server
// reads in FilterOperator::split().
const SPLIT_PATTERN = /(?<!\\)\|/;
const ESCAPED_PIPE = /\\\|/g;
const BARE_PIPE = /\|/g;
const NAMED_PATTERN = /^([a-z_]+):([\s\S]*)$/;

/** The operators a qualifier accepts, as descriptor objects. */
export function operatorsOf(qualifier) {
    return qualifier?.operators || [];
}

export function defaultOperatorOf(qualifier) {
    return qualifier?.default_operator || operatorsOf(qualifier)[0]?.value || '=';
}

export function operatorMeta(qualifier, token) {
    return operatorsOf(qualifier).find(operator => operator.value === token) || null;
}

/** Whether this operator still needs a value ("est vide" does not). */
export function takesValue(qualifier, token) {
    const meta = operatorMeta(qualifier, token);

    return meta ? meta.arity > 0 : true;
}

/**
 * What the value looks like for this operator: nothing, one value, a list, or a
 * range. The server says which — the shapes are declared alongside the operator
 * — so a control never has to keep its own table of what "entre" means.
 */
export function valueShapeOf(qualifier, token) {
    const meta = operatorMeta(qualifier, token);

    if (!meta) {
        return 'single';
    }

    if (meta.arity === 0) {
        return 'none';
    }

    if (meta.multi) {
        return 'list';
    }

    return meta.arity === 2 ? 'range' : 'single';
}

/** The values behind a list (`a|b`) or a range (`a..b`). */
export function splitValue(raw, separator = '|') {
    const text = String(raw ?? '');

    if (text === '') {
        return [];
    }

    const parts = separator === '|' ? text.split(SPLIT_PATTERN) : text.split(separator);

    return parts.map(part => part.replace(ESCAPED_PIPE, '|').trim()).filter(part => part !== '');
}

export function joinValue(values, separator = '|') {
    return values
        .filter(value => value !== '' && value !== null && value !== undefined)
        .map(value => (separator === '|' ? String(value).replace(BARE_PIPE, '\\|') : String(value)))
        .join(separator);
}

export function operatorLabel(qualifier, token) {
    return operatorMeta(qualifier, token)?.label || '';
}

/**
 * Split a raw URL value into the operator the filter accepts and the rest.
 * An operator the filter does not declare is left in the value, so nothing
 * changes meaning behind the user's back.
 */
export function parseFilterValue(raw, qualifier) {
    const fallback = { operator: defaultOperatorOf(qualifier), value: raw ?? '' };
    const text = String(raw ?? '');

    if (!text) {
        return fallback;
    }

    const accepted = token => operatorsOf(qualifier).some(operator => operator.value === token);

    const named = NAMED_PATTERN.exec(text);
    if (named) {
        // A named comparison resolves to the symbol the filter declares.
        const token = accepted(named[1]) ? named[1] : NAMED_SYMBOLS[named[1]];

        if (token && accepted(token)) {
            return { operator: token, value: takesValue(qualifier, token) ? named[2].trim() : '' };
        }
    }

    if (accepted(text) && !takesValue(qualifier, text)) {
        return { operator: text, value: '' };
    }

    const symbol = SYMBOL_PATTERN.exec(text);
    if (symbol && accepted(symbol[1])) {
        return { operator: symbol[1], value: symbol[2].trim() };
    }

    return fallback;
}

/**
 * What an operator looks like at the head of a typed value: the symbol on its
 * own (`!=`), or the name and a colon (`not_contains:`).
 */
export function operatorPrefix(token) {
    return SYMBOLS.includes(token) ? token : `${token}:`;
}

/**
 * Build the URL value. The default operator stays implicit and the comparison
 * symbols keep their historical form, so an existing link and a freshly built
 * one look the same.
 */
export function serializeFilterValue(operator, value, qualifier) {
    const token = operator || defaultOperatorOf(qualifier);

    if (!takesValue(qualifier, token)) {
        return token;
    }

    const text = value === null || value === undefined ? '' : String(value);

    if (text === '') {
        return '';
    }

    if (token === defaultOperatorOf(qualifier)) {
        return text;
    }

    return SYMBOLS.includes(token) ? `${token}${text}` : `${token}:${text}`;
}
